import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';

import { getSiteCloudId, MAX_SELECTED_SITES } from '../../../../../common/constants/filters/site';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useCloudId,
	useFiltersDiscoverability2Enabled,
	useUnitMetadata,
} from '../../../../../controllers/store/bootstrap';
import { siteFilterId } from '../../../../../controllers/store/search/filters/filter-ids';

import { i18n } from './messages';
import {
	buildCurrentSiteOption,
	SELECTED_GROUP,
	SelectedHeading,
	SiteOptionLabel,
	sortAlphabetically,
	UNSELECTED_GROUP,
} from './utils';

const SiteSelectFilter = ({
	isFilterInDropdown,
	onFilterChange,
	hideOnHoverOut,
	defaultIsOpen,
	collaborationContext,
}: FilterProps & {
	defaultIsOpen?: boolean;
	collaborationContext?: { orgName?: string; unitName?: string };
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [primarySiteId] = useCloudId();
	const unitMetadata = useUnitMetadata();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const { orgName, unitName } = collaborationContext ?? {};
	const primarySiteName =
		unitMetadata?.find((site) => getSiteCloudId(site.id) === primarySiteId)?.siteName ??
		primarySiteId;
	const [filterData, { loadInitialOptions, setOption: setOptionForSelectFilter }] = useSelectFilter(
		{
			filterId: siteFilterId,
		},
	);

	// Custom rendering of the first row displaying the current site to append "(current)" as italicised
	const formatOptionLabel = useMemo(
		() =>
			(option: {
				label?: React.ReactNode;
				value?: string | number;
				isSelected?: boolean;
			}): React.ReactNode => (
				<SiteOptionLabel option={option} isCurrent={option.value === primarySiteId} />
			),
		[primarySiteId],
	);

	// Only show the "(Select up to N)" hint when the tenant has enough sites to reach the cap.
	const capReachable = (unitMetadata?.length ?? 0) >= MAX_SELECTED_SITES;

	if (!filterData) {
		return null;
	}

	const { selectedOptions, options, isLoading } = filterData;

	const currentSiteOption = buildCurrentSiteOption(primarySiteId, primarySiteName);

	const selectedSites = selectedOptions
		.filter((option) => option.value !== primarySiteId)
		.sort(sortAlphabetically);

	const unselectedSites = options
		.filter((option) => option.value !== primarySiteId && !option.isSelected)
		.sort(sortAlphabetically);

	const selectedGroupOptions = currentSiteOption
		? [
				currentSiteOption, // Current site is always displayed first
				...selectedSites,
			]
		: selectedSites;

	const atCap = selectedGroupOptions.length >= MAX_SELECTED_SITES;
	const unselectedGroupOptions = atCap
		? unselectedSites.map((option) => ({
				...option,
				isDisabled: true, // Disable the rest of the options if over the cap
			}))
		: unselectedSites;

	const groupedOptions = [
		{ label: SELECTED_GROUP, options: selectedGroupOptions },
		{ label: UNSELECTED_GROUP, options: unselectedGroupOptions },
	].filter((group) => group.options.length > 0);

	return (
		<SelectFilter
			contentFit="compact"
			checkboxIndicators
			defaultIsOpen={defaultIsOpen}
			onDropdownOpen={loadInitialOptions}
			onSelectChange={(option) => {
				const changed = Array.isArray(option) ? option[0] : option;
				// Never route the current/primary site into the filter — it is display-only and must stay selected
				if (!changed || changed.value === primarySiteId) {
					return;
				}
				// Cap backstop: disabled options already block the click; guard selecting a new site at the cap.
				if (atCap && !changed.isSelected) {
					return;
				}
				setOptionForSelectFilter(changed);
				onFilterChange?.(changed);
			}}
			options={groupedOptions}
			selectedOptions={selectedGroupOptions}
			label={primarySiteName || formatMessage(i18n.label)}
			filterOrControlId={'site'}
			isLoading={isLoading}
			formatOptionLabel={formatOptionLabel}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			hideOnHoverOut={hideOnHoverOut}
			iconBefore={<OfficeBuildingIcon label="" />}
			// The unselected group is titled by the collaboration unit/org name when available, otherwise
			// "Site"/"Sites" (pluralised on the count).
			formatGroupLabel={(group) =>
				group.label === SELECTED_GROUP ? (
					<SelectedHeading atCap={atCap} capReachable={capReachable} />
				) : (
					(unitName ??
					orgName ??
					formatMessage(i18n.unselectedHeading, { count: group.options.length }))
				)
			}
			// Count every selected site for the +N badge, but keep the chip greyed until more than the
			// current site is picked.
			hasNeutralBackground={selectedGroupOptions.length <= 1}
			isSelectedOverride={selectedGroupOptions.length > 1}
			getFirstSelectedLabel={() => ''}
		/>
	);
};

export default SiteSelectFilter;
